import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Package, Truck, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionLineType,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';

import StatusProgressionFlow from './StatusProgressionFlow';
import HistoricalTimelineFlow from './HistoricalTimelineFlow';
import { orderService } from '../../services/orderService';
import { showError } from '../../utils/notificationHandler';

const OrderStatusFlowModal = ({ isOpen, onClose, order }) => {
  const [activeTab, setActiveTab] = useState('progression');
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [fulfillmentMetrics, setFulfillmentMetrics] = useState(null);

  // Fetch enhanced order data when modal opens
  useEffect(() => {
    if (isOpen && order) {
      fetchOrderData();
    }
  }, [isOpen, order]);

  const fetchOrderData = async () => {
    try {
      setLoading(true);
      
      // Get detailed order information with fulfillment metrics (portal orders only)
      const [details, history, metrics] = await Promise.all([
        orderService.getOrderDetails(order.order_number),
        orderService.getOrderStatusHistory(order.order_number),
        orderService.getOrderFulfillmentDetails(order.order_number)
      ]);

      setOrderDetails(details);
      setStatusHistory(history);
      setFulfillmentMetrics(metrics);
      
    } catch (error) {
      console.error('Error fetching order data:', error);
      showError(error, 'Failed to load portal order tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchOrderData();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] relative flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <Package className="h-6 w-6 text-brand" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Order Tracking: {order?.order_number}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Portal order status progression and history
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    title="Refresh tracking data"
                  >
                    <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('progression')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors duration-200 ${
                    activeTab === 'progression'
                      ? 'border-brand text-brand-dark bg-brand/10'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Truck className="h-4 w-4" />
                    <span>Status Progression</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors duration-200 ${
                    activeTab === 'timeline'
                      ? 'border-brand text-brand-dark bg-brand/10'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>Historical Timeline</span>
                  </div>
                </button>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading portal order tracking data...</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full">
                    {activeTab === 'progression' ? (
                      <StatusProgressionFlow
                        order={orderDetails}
                        fulfillmentMetrics={fulfillmentMetrics}
                        onRefresh={handleRefresh}
                      />
                    ) : (
                      <HistoricalTimelineFlow
                        order={orderDetails}
                        statusHistory={statusHistory}
                        onRefresh={handleRefresh}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {fulfillmentMetrics && (
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                          {fulfillmentMetrics.fulfillment_percentage || 0}% Complete
                        </span>
                        {fulfillmentMetrics.total_lines > 0 && (
                          <span className="text-gray-500">
                            {fulfillmentMetrics.fully_shipped_lines || 0} of {fulfillmentMetrics.total_lines} items shipped
                          </span>
                        )}
                        {order?.total_backordered_qty > 0 && (
                          <span className="flex items-center text-orange-600">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {order.total_backordered_qty} units on backorder
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    Last updated: {new Date().toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default OrderStatusFlowModal;
